import React from "react";
import { graphql } from "react-relay";
import TextArea from "react-textarea-autosize";

import {
	faChevronsDown,
	faChevronsUp,
	faClone,
	faInfoCircle,
	faPen,
	faSpinnerThird,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCut, faMagnifyingGlassPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faChevronDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import * as Sentry from "@sentry/browser";
import cx from "classnames";
import Markdown from "markdown-to-jsx";

import { AppContext } from "~/common";
import { useResizeObserver } from "~/lib/hooks";
import { commitMutationPromise } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { HighlightSection, RegenerateButtons } from "~/meetingRecorder/HighlightsPanel";
import { type KeyEvent, VideoTimestamp } from "~/meetingRecorder/TranscriptPanel";
import type { ChapterWithDurationAndRef } from "~/meetingRecorder/Video/Timeline";
import { environment } from "~/models";
import { Button, Tooltip } from "~/ui";
import { IconButton, ToggleGroup } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";
import toasty from "~/ui/toast";

import { ContextPopover } from "./ContextPopover";
import { HIGHLIGHTS_MARKDOWN_OPTIONS } from "./HighlightsPanel";
import type { TranscriptChapter } from "./TranscriptModal";
import { RecordingContext, TranscriptModalContext } from "./TranscriptModalContext";
import {
	addTextTrackCuesToChapters,
	type BulletPoint,
	chaptersToText,
	emptyHighlightSectionText,
	formatTimestamp,
	HighlightFeedbackSource,
	useIsNarrowViewport,
	useIsPublic,
} from "./utils";

import type { ChaptersPanel_meetingRecording$data as ChaptersPanelMeetingRecording } from "~/__generated__/ChaptersPanel_meetingRecording.graphql";
import type { ChaptersPanelSaveChaptersMutation } from "~/__generated__/ChaptersPanelSaveChaptersMutation.graphql";

import styles from "./css/ChaptersPanel.module.css";
import highlightsStyles from "./css/HighlightsPanel.module.css";

const saveChaptersMutation = graphql`
	mutation ChaptersPanelSaveChaptersMutation($recordingId: ID!, $chaptersText: String!) {
		saveChapters(recordingId: $recordingId, chaptersText: $chaptersText) {
			validationError
			meetingRecording {
				id
				activeSummary {
					chapters {
						timestamp
						title
						bulletPoints {
							text
							timestamp
							textShort
							context
						}
					}
				}
				...HighlightsPanel_meetingRecording @arguments(public: false)
			}
		}
	}
`;

type ChapterWithBulletPoints = TranscriptChapter & {
	bulletPoints: readonly BulletPoint[];
};

type ChapterRowProps = {
	chapter: ChapterWithBulletPoints;
	isOpen: boolean;
	toggleOpen: (timestamp: number | string) => void;
	active: boolean;
	onTimestampClick: (timestamp: number) => void;
	videoDuration: number | null;
	isShortSummary: boolean;
	recordingId: string;
	index: number;
	chapterRowId?: string;
	mode: "edit" | "readonly";
};

function ChapterRow({
	chapter,
	isOpen,
	toggleOpen,
	active,
	onTimestampClick,
	videoDuration,
	isShortSummary,
	recordingId,
	index: chapterIndex,
	mode,
}: ChapterRowProps) {
	const summaryXrayEnabled = useFeatureIsOn("summarizer-xray");
	const isPublic = useIsPublic();
	// UX Simplification
	const { user } = React.useContext(AppContext);
	const uxSimplificationEnabled = user?.uxSimplificationEnabled;

	const onTimeClick = React.useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			if (chapter.timestamp != null) {
				onTimestampClick(chapter.timestamp);
				event.stopPropagation();
			}
		},
		[onTimestampClick, chapter.timestamp],
	);

	const onToggleOpen = React.useCallback(() => {
		if (chapter.timestamp != null) {
			toggleOpen(chapter.timestamp);
		} else {
			toggleOpen(`topic-${chapter.title.replace(/\s+/g, "-").toLowerCase()}`);
		}
	}, [chapter.timestamp, chapter.title, toggleOpen]);

	const { copySection, openClipCutModal } = React.useContext(RecordingContext);
	const onCopyClicked = React.useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			copySection("SingleChapter", {
				singleChapterTitle: chapter.timestamp == null ? chapter.title : undefined,
				singleChapterTimestamp: chapter.timestamp ?? undefined,
			});

			event.stopPropagation();
		},
		[chapter, copySection],
	);

	const onCreateClipClicked = React.useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			if (chapter.timestamp != null && chapter.duration != null) {
				openClipCutModal(chapter.timestamp, chapter.timestamp + chapter.duration);
			}
			event.stopPropagation();
		},
		[chapter, openClipCutModal],
	);

	// Format the timestamp if it exists
	const ts = chapter.timestamp;
	let formattedTime = null;
	if (ts != null) {
		const chapterSeconds = Math.floor(ts % 60);
		const chapterMinutes = Math.floor(ts / 60) % 60;
		const chapterHours = Math.floor(ts / 3600);

		const parts = [
			...(chapterHours ? [chapterHours] : []),
			chapterMinutes.toString().padStart(2, "0"),
			chapterSeconds.toString().padStart(2, "0"),
		];

		formattedTime = parts.join(":");
	}

	const chapterRowId = `chapter-${ts ? ts : `topic-${chapter.title.replace(/\s+/g, "-").toLowerCase()}`}`;

	return (
		<div className={styles.chapterRowWrapper} id={chapterRowId}>
			<div className={styles.chapterRow} onClick={onToggleOpen}>
				{!uxSimplificationEnabled && (
					<FontAwesomeIcon
						fixedWidth
						icon={faChevronDown}
						className={cx(styles.chevron, { [styles.collapsed]: !isOpen })}
					/>
				)}

				<div className={styles.chapterTitle}>{chapter.title}</div>
				{formattedTime && (
					<div className={cx(styles.chapterTime, { [styles.active]: active })} onClick={onTimeClick}>
						{formattedTime}
					</div>
				)}

				{uxSimplificationEnabled && (
					<FontAwesomeIcon
						fixedWidth
						icon={faChevronDown}
						className={cx(styles.chevron, { [styles.collapsed]: !isOpen })}
					/>
				)}

				<div className={styles.chapterButtons}>
					{chapter.timestamp != null && mode === "edit" && (
						<Tooltip content="Create a clip">
							<IconButton
								roundBackground
								title="Create a clip"
								icon={faCut}
								className={styles.chapterButton}
								onClick={onCreateClipClicked}
							/>
						</Tooltip>
					)}
					<Tooltip content="Copy">
						<IconButton
							roundBackground
							title="Copy"
							icon={faClone}
							className={styles.chapterButton}
							onClick={onCopyClicked}
						/>
					</Tooltip>
				</div>
			</div>
			{isOpen && (
				<div className={styles.chapterBulletsWrapper}>
					<ul className={styles.chapterBullets}>
						{!chapter.bulletPoints?.length && (
							<li className={styles.placeholderBullet}>
								Describe a key moment that happened in this chapter that you want to flag to your team
							</li>
						)}
						{chapter.bulletPoints.map((bulletPoint, index) => (
							// eslint-disable-next-line react/no-array-index-key
							<li key={`chapter-point-${index}`}>
								<div className={styles.chapterBulletPointRow} id={`${chapterRowId}-bullet-${index}`}>
									<div className={styles.bulletTextWrapper}>
										<Markdown options={HIGHLIGHTS_MARKDOWN_OPTIONS} className={styles.chapterBulletPoint}>
											{isShortSummary ? bulletPoint.textShort || bulletPoint.text : bulletPoint.text}
										</Markdown>
										{bulletPoint.timestamp != null && (
											<VideoTimestamp
												className={styles.chapterTimestamp}
												timestamp={bulletPoint.timestamp}
												videoDuration={videoDuration}
												onClick={onTimestampClick}
												isInlineTimestamp={true}
											/>
										)}
									</div>
									{summaryXrayEnabled && !isPublic && (
										<ContextPopover
											context={bulletPoint.context ?? ""}
											onTimestampClick={onTimestampClick}
											itemText={bulletPoint.text}
											timestamp={bulletPoint.timestamp ? formatTimestamp(bulletPoint.timestamp) : undefined}
											recordingId={recordingId}
											lookupKey={
												chapter.bulletPoints ? [chapterIndex, chapter.bulletPoints.indexOf(bulletPoint)] : undefined
											}
											chapterRowId={`${chapterRowId}-bullet-${index}`}
										>
											<Tooltip content="X-Ray" delay={400}>
												<FontAwesomeIcon icon={faMagnifyingGlassPlus} className={styles.contextIcon} tabIndex={0} />
											</Tooltip>
										</ContextPopover>
									)}
								</div>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
}

// Update the FullChapter type to use our modified interface
type FullChapter = ChapterWithDurationAndRef & {
	cue: VTTCue | null;
	bulletPoints: readonly BulletPoint[];
};

// Fix the selectChapterFromTimestamp function for proper null handling
function selectChapterFromTimestamp(chapters: FullChapter[] | undefined, timestamp: number): FullChapter | undefined {
	return chapters?.find((chapter, chapterIndex) => {
		// If timestamp is undefined or null, skip this chapter when selecting by timestamp
		if (chapter.timestamp == null) return false;

		const isFirstChapter = chapterIndex === 0 && timestamp <= chapter.timestamp;
		const nextChapter = chapters[chapterIndex + 1];
		const nextChapterTimestamp = nextChapter?.timestamp;

		const currentTimeBetweenThisChapterAndNext =
			timestamp >= chapter.timestamp &&
			(chapterIndex + 1 === chapters.length || nextChapterTimestamp == null || timestamp <= nextChapterTimestamp);

		return isFirstChapter || currentTimeBetweenThisChapterAndNext;
	});
}

export interface ChaptersPanelRef {
	handleTimeUpdate: (currentTime: number) => void;
}

export type TranscriptChapters = NonNullable<ChaptersPanelMeetingRecording["activeSummary"]>["chapters"];

interface ChaptersPanelProps {
	recordingId: string;
	chapters: TranscriptChapters;
	onTimestampClick: (timestamp: number) => void;
	onDislike: (source: HighlightFeedbackSource) => void;
	videoDuration: number | null;
	thumbnailTrack: TextTrack | null;
	userVideoProgress: number;
	mode: "edit" | "readonly";
	collapsible?: boolean;
	noVideo?: boolean;
	recordingTitle: string;
	chaptersScrollDisabled?: boolean;
}

graphql`
	fragment ChaptersPanel_meetingRecording on MeetingRecording {
		id
		activeSummary {
			chapters {
				timestamp
				title
				bulletPoints {
					text
					timestamp
					textShort
					context
				}
			}
		}
		userVideoProgress
	}
`;

export const ChaptersPanel = React.forwardRef<ChaptersPanelRef, ChaptersPanelProps>(function ChaptersPanel(
	{
		recordingId,
		chapters: chaptersData,
		onTimestampClick,
		onDislike,
		videoDuration,
		thumbnailTrack,
		userVideoProgress,
		mode,
		collapsible,
		noVideo,
		recordingTitle,
		chaptersScrollDisabled,
	},
	ref,
) {
	const { analyticsFingerprint, noteId, meetingRecordingSummaryId } = React.useContext(TranscriptModalContext);
	const isNarrowViewport = useIsNarrowViewport();

	const [chapters, selectedChapter] = useChapters(chaptersData, videoDuration, thumbnailTrack, userVideoProgress, ref);

	// UX Simplification
	const { user } = React.useContext(AppContext);
	const uxSimplificationEnabled = user?.uxSimplificationEnabled;

	// Check if we have any short summaries available
	const hasShortSummaries = React.useMemo(() => {
		if (!chapters?.length) return false;
		const firstChapter = chapters[0];
		return firstChapter.bulletPoints.some(bullet => bullet.textShort && bullet.textShort.trim() !== "");
	}, [chapters]);

	const [isShortSummary, setIsShortSummary] = React.useState(hasShortSummaries);

	// Update isShortSummary when chapters change
	React.useEffect(() => {
		setIsShortSummary(hasShortSummaries);
	}, [hasShortSummaries]);

	const handleSummaryToggle = React.useCallback((value: string) => {
		setIsShortSummary(value === "short");
	}, []);

	const [chaptersText, setChaptersText] = React.useState<string>(chaptersToText(chapters ?? [], videoDuration));
	const prevChapterText = React.useRef<string>(chaptersText);
	const [chaptersOpenState, setChaptersOpenState] = React.useState<Record<string, boolean>>({});

	// Update chaptersOpenState when chapters change
	React.useEffect(() => {
		if (!chapters) return;

		setChaptersOpenState(prevState => {
			const newState = { ...prevState };
			chapters.forEach((chapter, index) => {
				const key =
					chapter.timestamp != null
						? chapter.timestamp.toString()
						: `topic-${chapter.title.replace(/\s+/g, "-").toLowerCase()}`;

				// If this is a new chapter/topic not in previous state
				if (newState[key] === undefined) {
					// only pre-expand the first topic
					newState[key] = index === 0;
				}
			});
			return newState;
		});
	}, [chapters]);

	const toggleChaptersOpenState = React.useCallback(
		(timestamp: number | string) => {
			setChaptersOpenState(prevState => {
				const key = typeof timestamp === "number" ? timestamp.toString() : timestamp;

				return {
					...prevState,
					[key]: !prevState[key],
				};
			});
			dispatchAnalyticsTrack("[AI Copilot] Chapter manually expanded", {
				fingerprint: analyticsFingerprint,
				source: mode === "readonly" ? "Full page (public view)" : "Modal",
			});
		},
		[analyticsFingerprint, mode],
	);

	const setChaptersValue = React.useCallback((chapters: TranscriptChapter[], value: boolean) => {
		setChaptersOpenState(
			chapters.reduce(
				(map, chapter) => {
					if (chapter.timestamp != null) {
						map[chapter.timestamp] = value;
					} else {
						// Use a string key for topics without timestamps
						map[`topic-${chapter.title.replace(/\s+/g, "-").toLowerCase()}`] = value;
					}
					return map;
				},
				{} as Record<string, boolean>,
			),
		);
	}, []);

	const expandAllChapters = React.useCallback(() => {
		if (!chapters) return;
		setChaptersValue(chapters, true);
		dispatchAnalyticsTrack("[AI Copilot] All chapters manually expanded", {
			fingerprint: analyticsFingerprint,
			source: mode === "readonly" ? "Full page (public view)" : "Modal",
		});
	}, [analyticsFingerprint, chapters, mode, setChaptersValue]);

	const collapseAllChapters = React.useCallback(() => {
		if (!chapters) return;

		setChaptersValue(chapters, false);
	}, [chapters, setChaptersValue]);

	const isAllChaptersExpanded = React.useMemo(() => {
		return chapters?.every((chapter: TranscriptChapter) => {
			if (chapter.timestamp != null) {
				return chaptersOpenState[chapter.timestamp.toString()];
			} else {
				// Use a string key for topics without timestamps
				return chaptersOpenState[`topic-${chapter.title.replace(/\s+/g, "-").toLowerCase()}`];
			}
		});
	}, [chapters, chaptersOpenState]);

	const updateChapters = React.useCallback(
		(chaptersText: string) => {
			return commitMutationPromise<ChaptersPanelSaveChaptersMutation>(environment, {
				mutation: saveChaptersMutation,
				variables: {
					recordingId: recordingId,
					chaptersText: chaptersText,
				},
			});
		},
		[recordingId],
	);

	const onSaveEdit = React.useCallback(() => {
		if (mode !== "edit") return;
		setLoading(true);
		updateChapters(chaptersText)
			.then(res => {
				setLoading(false);

				if (!res.saveChapters) {
					toasty.error("Something went wrong, failed to update chapters");
					return;
				}

				const { validationError, meetingRecording } = res.saveChapters;

				if (validationError) {
					setError(validationError);
				} else if (meetingRecording?.activeSummary?.chapters) {
					setEditMode(false);
					prevChapterText.current = chaptersToText(
						meetingRecording.activeSummary.chapters as unknown as readonly ChapterWithBulletPoints[],
						videoDuration,
					);
					toasty.success("Chapters updated successfully");
					dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
						fingerprint: analyticsFingerprint,
						action: "Saved",
						/*  eslint-disable camelcase */
						recap_component: "Chapter",
						ux_location: "chapters_panel",
						note_id: noteId,
						meeting_recording_summary_id: meetingRecordingSummaryId,
						/*  eslint-enable camelcase */
					});
				}
			})
			.catch(errors => {
				setLoading(false);
				Sentry.captureException(errors[0]);
				toasty.error("Something went wrong, failed to update chapters");
			});
	}, [mode, updateChapters, chaptersText, videoDuration, analyticsFingerprint, noteId, meetingRecordingSummaryId]);

	const onEditChapters = React.useCallback(() => {
		if (mode === "readonly") return;
		setEditMode(true);

		// Hacky way to set the cursor to the end of the textarea when editing
		// The textarea is not rendered until editMode is true, so we need to wait
		// for the element to render to have access to the ref
		setTimeout(() => {
			textareaRef.current?.setSelectionRange(chaptersText.length, chaptersText.length);
		}, 0);
		dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
			fingerprint: analyticsFingerprint,
			action: "Started",
			/*  eslint-disable camelcase */
			recap_component: "Chapter",
			ux_location: "chapters_panel",
			note_id: noteId,
			meeting_recording_summary_id: meetingRecordingSummaryId,
			/*  eslint-enable camelcase */
		});
	}, [mode, analyticsFingerprint, noteId, meetingRecordingSummaryId, chaptersText.length]);

	const onChangeChaptersText = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setChaptersText(event.target.value);
	}, []);

	const onInputChaptersText = React.useCallback((): void => {
		setError(null);
	}, []);

	const onCancelEdit = React.useCallback(() => {
		setEditMode(false);
		setError(null);
		setChaptersText(prevChapterText.current);
		dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
			fingerprint: analyticsFingerprint,
			action: "Cancelled",
			/*  eslint-disable camelcase */
			recap_component: "Chapter",
			ux_location: "chapters_panel",
			note_id: noteId,
			meeting_recording_summary_id: meetingRecordingSummaryId,
			/*  eslint-enable camelcase */
		});
	}, [analyticsFingerprint, meetingRecordingSummaryId, noteId]);

	const [editMode, setEditMode] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);
	const [loading, setLoading] = React.useState<boolean>(false);
	const textareaRef = React.useRef<HTMLTextAreaElement>(null);

	const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);
	const toggleCollapsed = React.useCallback(() => {
		if (editMode) return;
		setIsCollapsed(!isCollapsed);
	}, [editMode, isCollapsed]);

	React.useEffect(() => {
		// Updates received from other users' edits
		if (!editMode) {
			const newChapters = chaptersToText(chapters ?? [], videoDuration);
			setChaptersText(newChapters);
			prevChapterText.current = newChapters;
		}
	}, [editMode, chapters, videoDuration]);

	React.useEffect(() => {
		if (!selectedChapter) return;

		// Auto-open the section when changing chapters
		setChaptersOpenState(prevState => {
			return {
				...prevState,
				[selectedChapter.timestamp?.toString() ?? `topic-${selectedChapter.title.replace(/\s+/g, "-").toLowerCase()}`]:
					true,
			};
		});

		if (isNarrowViewport || chaptersScrollDisabled) return;

		setTimeout(() => {
			document.getElementById(`chapter-${selectedChapter.timestamp}`)?.scrollIntoView({ behavior: "smooth" });
		}, 200);
	}, [selectedChapter, isNarrowViewport, chaptersScrollDisabled]);

	const { copySection } = React.useContext(RecordingContext);
	const onCopyChapters = React.useCallback(() => {
		copySection("Chapters");
	}, [copySection]);
	const onCopyAll = React.useCallback(() => {
		copySection("All");
	}, [copySection]);

	const [headerHeight, trackHeaderHeight] = React.useState<number>();
	const { ref: chaptersHeaderRef } = useResizeObserver<HTMLElement>({
		box: "border-box",
		onResize: ({ height }) => trackHeaderHeight(height),
	});

	const noChapters = chapters == null || chapters.length === 0;

	const chaptersTooltip = <>Fellow will automatically generate AI notes and display them here.</>;
	const chaptersButtons = React.useMemo(() => {
		// Define editButton inside the useMemo
		const editButton = isNarrowViewport ? (
			<IconButton title="Edit" icon={faPen} onClick={onEditChapters} className={highlightsStyles.addAction} />
		) : (
			<Button variant="tertiary" size="slim" onClick={onEditChapters} className={styles.chapterButton}>
				<FontAwesomeIcon icon={faPen} />
				<span>&nbsp; Edit</span>
			</Button>
		);

		if (editMode) {
			return (
				<div className={styles.btnWrapper} key="chapters-buttons">
					<Button variant="secondary" size="slim" disabled={loading} onClick={onCancelEdit}>
						Cancel
					</Button>

					<Button
						variant="primary"
						size="slim"
						disabled={!!error || loading || chaptersText === prevChapterText.current}
						onClick={onSaveEdit}
					>
						{loading ? <FontAwesomeIcon icon={faSpinnerThird} spin /> : "Save"}
					</Button>
				</div>
			);
		} else if (noChapters) {
			if (mode !== "readonly") {
				return (
					<Button variant="secondary" size="slim" onClick={onEditChapters} key="chapters-buttons">
						+ Add
					</Button>
				);
			}
			return null;
		} else {
			// have chapters
			return (
				<div className={styles.btnWrapper} key="chapters-buttons">
					{mode === "edit" && (
						<RegenerateButtons
							clickDislike={onDislike}
							sectionName={HighlightFeedbackSource.CHAPTERS}
							recordingId={recordingId}
							recordingTitle={recordingTitle}
						/>
					)}
					{isAllChaptersExpanded ? (
						<Tooltip content="Collapse all">
							<IconButton
								roundBackground
								className={styles.chapterButton}
								title="Collapse all"
								icon={faChevronsUp}
								onClick={collapseAllChapters}
							/>
						</Tooltip>
					) : (
						<Tooltip content="Expand all">
							<IconButton
								roundBackground
								className={styles.chapterButton}
								title="Expand all"
								icon={faChevronsDown}
								onClick={expandAllChapters}
							/>
						</Tooltip>
					)}
					<Menu>
						<Menu.Trigger>
							<IconButton roundBackground title="Copy" icon={faClone} className={styles.chapterButton} />
						</Menu.Trigger>
						<Menu.Content align="end">
							<Menu.Item onSelect={onCopyChapters}>Copy Chapters</Menu.Item>
							<Menu.Item onSelect={onCopyAll}>Copy entire Recap</Menu.Item>
						</Menu.Content>
					</Menu>

					{mode !== "readonly" && editButton}

					{hasShortSummaries && (
						<ToggleGroup
							className={styles.summaryToggle}
							options={[
								{ value: "short", label: "Short" },
								{ value: "long", label: "Long" },
							]}
							value={isShortSummary ? "short" : "long"}
							onValueChange={handleSummaryToggle}
						/>
					)}
				</div>
			);
		}
	}, [
		isNarrowViewport,
		onEditChapters,
		editMode,
		noChapters,
		loading,
		onCancelEdit,
		error,
		chaptersText,
		onSaveEdit,
		mode,
		onDislike,
		recordingId,
		recordingTitle,
		isAllChaptersExpanded,
		collapseAllChapters,
		expandAllChapters,
		onCopyChapters,
		onCopyAll,
		hasShortSummaries,
		isShortSummary,
		handleSummaryToggle,
	]);

	const timePlaceHolder = videoDuration ? `[${videoDuration >= 3600 ? "00:" : ""}00:00]` : "[00:00]";

	const sectionContent = editMode ? (
		<>
			<main className={cx(styles.textAreaWrapper, { [styles.error]: error })}>
				<TextArea
					autoFocus
					ref={textareaRef}
					value={chaptersText}
					onChange={onChangeChaptersText}
					onInput={onInputChaptersText}
					className={styles.textArea}
					minRows={4}
					placeholder={`Topic title\n- ${timePlaceHolder} Key moment`}
				/>
			</main>
			{error ? <p className={styles.errorText}>{error}</p> : null}
		</>
	) : noChapters ? (
		<div className={styles.noChaptersBanner}>
			{emptyHighlightSectionText(videoDuration, "topics")}
			{mode !== "readonly" && (
				<>
					<br />
					To{" "}
					<span className={styles.bannerLink} onClick={onEditChapters}>
						add a topic
					</span>
					, follow the example below.
					<br /> <br />
					For example: <br />
					<code className={styles.codeExample}>
						Topic One
						<br />
						- [2:34] Key Moment One <br />- [3:45] Key Moment Two
					</code>
				</>
			)}
		</div>
	) : (
		<div>
			{chapters.map((chapter: FullChapter, index: number) => (
				<ChapterRow
					active={chapter === selectedChapter && !Boolean(noVideo)}
					isOpen={
						chaptersOpenState[
							chapter.timestamp != null
								? chapter.timestamp.toString()
								: `topic-${chapter.title.replace(/\s+/g, "-").toLowerCase()}`
						]
					}
					toggleOpen={toggleChaptersOpenState}
					key={
						chapter.timestamp != null
							? chapter.timestamp.toString()
							: `topic-${chapter.title.replace(/\s+/g, "-").toLowerCase()}`
					}
					chapter={chapter}
					onTimestampClick={onTimestampClick}
					videoDuration={videoDuration}
					isShortSummary={isShortSummary}
					recordingId={recordingId}
					index={index}
					mode={mode}
				/>
			))}
		</div>
	);

	// Check if there are any chapters with non-zero timestamps
	const hasNonZeroTimestamps = React.useMemo(() => {
		return chapters?.some(chapter => chapter.timestamp !== null && chapter.timestamp > 0);
	}, [chapters]);

	if (collapsible) {
		return (
			<HighlightSection
				isCollapsed={isCollapsed}
				toggleCollapsed={toggleCollapsed}
				title={hasNonZeroTimestamps ? "Chapters" : "Topics"}
				secondaryActions={chaptersButtons ? [chaptersButtons] : undefined}
			>
				<div className={styles.collapsibleWrapper}>{sectionContent}</div>
			</HighlightSection>
		);
	}

	return (
		<section
			style={{ "--headerHeight": `${headerHeight}px` }}
			className={cx(styles.chaptersContainer, { [styles.simplifiedUxContainer]: uxSimplificationEnabled })}
		>
			<div ref={chaptersHeaderRef} className={styles.chaptersHeader}>
				<div className={styles.chaptersTitle}>
					<div className={styles.chaptersHeading}>{hasNonZeroTimestamps ? "Chapters" : "Topics"}</div>
					{mode !== "readonly" && (
						<Tooltip maxWidth={240} content={chaptersTooltip}>
							<FontAwesomeIcon icon={faInfoCircle} className={styles.chaptersIcon} />
						</Tooltip>
					)}
				</div>
				{chaptersButtons}
			</div>
			<div className={styles.chaptersBody}>{sectionContent}</div>
		</section>
	);
});

type ChapterType = NonNullable<NonNullable<ChaptersPanelMeetingRecording["activeSummary"]>["chapters"]>[0];
function useChapters(
	recordingChapters: NonNullable<ChaptersPanelMeetingRecording["activeSummary"]>["chapters"],
	videoDuration: number | null,
	thumbnailTrack: TextTrack | null,
	userVideoProgress: number,
	ref: React.ForwardedRef<ChaptersPanelRef>,
) {
	const chapters = React.useMemo(() => {
		const chaptersWithRefs = recordingChapters?.map((chapter: ChapterType, i: number) => {
			const nextChapter = recordingChapters?.[i + 1];
			const { timestamp } = chapter;

			// For chapters without timestamps, we don't need to calculate duration
			const duration =
				timestamp != null && nextChapter?.timestamp != null
					? nextChapter.timestamp - timestamp
					: timestamp != null && videoDuration
						? videoDuration - timestamp
						: 0;

			return {
				...chapter,
				duration,
				ref: React.createRef<HTMLDivElement>(),
				cue: null as VTTCue | null,
				// eslint-disable-next-line camelcase
				keyEvents: null as KeyEvent[] | undefined | null, // TODO: fix types
			};
		});

		if (thumbnailTrack?.cues && chaptersWithRefs) {
			// Only add cues to chapters with timestamps
			return addTextTrackCuesToChapters(
				thumbnailTrack,
				chaptersWithRefs,
				(chapter: ChapterType) => chapter.timestamp ?? -1,
			);
		} else {
			return chaptersWithRefs;
		}
	}, [recordingChapters, thumbnailTrack, videoDuration]);

	const [selectedChapter, setSelectedChapter] = React.useState<NonNullable<typeof chapters>[number] | undefined>(
		selectChapterFromTimestamp(chapters, userVideoProgress),
	);

	// This effect updates the `selectedChapter` whenever the `chapters` memo object changes.
	React.useEffect(() => {
		if (!chapters) return;
		setSelectedChapter((selectedChapter: ChapterType | undefined) => {
			if (selectedChapter !== undefined) {
				// For chapters with timestamps, find by timestamp
				if (selectedChapter.timestamp != null) {
					const selectedChapterIndex = chapters.findIndex(
						(chapter: ChapterType) => chapter.timestamp === selectedChapter.timestamp,
					);
					if (selectedChapterIndex === -1) return undefined;
					return chapters[selectedChapterIndex];
				}
				// For topics without timestamps, find by title
				else {
					const selectedChapterIndex = chapters.findIndex(
						(chapter: ChapterType) => chapter.title === selectedChapter.title,
					);
					if (selectedChapterIndex === -1) return undefined;
					return chapters[selectedChapterIndex];
				}
			}
			return undefined;
		});
	}, [chapters]);

	React.useImperativeHandle(ref, () => ({
		handleTimeUpdate: (currentTime: number) => {
			setSelectedChapter(selectChapterFromTimestamp(chapters, currentTime));
		},
	}));

	return [chapters, selectedChapter] as const;
}
